import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

// CONTEXT
import { AuthContext } from 'context/AuthContext';
import { ToastContext } from 'context/ToastContext';

// HOOKS
import useUserData from 'hooks/useUserData';

// COMPONENTS
import Loader from 'components/loaderComponent';
import Sidebar from 'components/edition/sidebarComponent';
import GridEditing from 'components/gridEditingComponent';
import ModeToggle from 'components/buttons/ModeToggle';
import ProposeTileModal from 'components/edition/ProposeTileModal';
import TooltipComponent from 'components/tooltipComponent';

// UTILS
import { MapPreferencesToTiles } from 'utils/mapPreferencesToTiles';

// ICONS
import { ChevronRight, Save, RefreshCcw, CircleX, Check, Plus } from 'lucide-react';

// SERVICES
import { getUnusedTiles, upsertUserPreferences, getDefaultTiles, proposeTile } from 'services/tileService';

function EditInterface() {
    const { addToast } = useContext(ToastContext);
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ maxWidth: 1024 });
    const { authenticated, email, isInternal, isExternal, isAdmin } = useContext(AuthContext);
    const { userADInfo, userBDDInfo, preferences } = useUserData(email, isInternal, isExternal, isAdmin);
    const [ loading, setLoading ] = useState(true);

    const [isSidebarOpen, setIsSidebarOpen] = useState(true)

    const [isDesktopMode, setIsDesktopMode] = useState(true);
    const [desktopLayout, setDesktopLayout] = useState([]);
    const [mobileLayout, setMobileLayout] = useState([]);
    const [layoutReady, setLayoutReady] = useState(false);
    const [unusedTiles, setUnusedTiles] = useState([]);
    const [tilesState, setTilesState] = useState([]);
    const [colorPickerTileId, setColorPickerTileId] = useState(null);
    const [showProposeModal, setShowProposeModal] = useState(false);
    const [showResetConfirmation, setShowResetConfirmation] = useState(false);
    const [initialDesktopLayout, setInitialDesktopLayout] = useState([]);
    const [initialMobileLayout, setInitialMobileLayout] = useState([]);
    const [initialUnusedTiles, setInitialUnusedTiles] = useState([]);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [hasInitialLayouts, setHasInitialLayouts] = useState(false);

    const availableColors = [
        { color: "#EF9700", id: 1 },
        { color: "#003B5E", id: 5 },
        { color: "#00BBBA", id: 9 },
        { color: "#DB0661", id: 13 },
        { color: "#B3B2BD", id: 17 }
    ];

    const getRandomColor = () => {
        const randomIndex = Math.floor(Math.random() * availableColors.length);
        return availableColors[randomIndex];
    };

    const currentLayout = isDesktopMode ? desktopLayout : mobileLayout;
    const setCurrentLayout = isDesktopMode ? setDesktopLayout : setMobileLayout;

    useEffect(() => {
        if (isMobile) {
            addToast('Information', 'L\'édition de l\'interface n\'est disponible que sur ordinateur. Veuillez utiliser un ordinateur pour modifier votre interface.', null, null, 'info');
            navigate('/');
        }
    }, [isMobile, navigate, addToast]);

    useEffect(() => {
        const fetchTiles = async () => {
            if (authenticated && userBDDInfo?.data?.[0]?.USR_ID && preferences) {
                const userId = userBDDInfo.data[0].USR_ID;
    
                const mapped = MapPreferencesToTiles(preferences);
                setTilesState(mapped);
    
                try {
                    const response = await getUnusedTiles(userId);
                    const unused = response.data;
                     
                    const formattedUnused = unused
                        .filter(t => !isExternal || t.TIL_AVAILABLE_FOR_PRESTA)
                        .map(t => {
                        let maxHeight = 3, minHeight = 1, maxWidth = 3, minWidth = 1;

                        switch (t.TYP_NAME) {
                            case 'information':
                            case 'link':
                                maxHeight = 2;
                                minHeight = 1;
                                maxWidth = 2;
                                minWidth = 1;
                                break;
                            case 'notification':
                                maxHeight = 6;
                                minHeight = 2;
                                maxWidth = 4;
                                minWidth = 4;
                                break;
                            case 'button':
                                maxHeight = 4;
                                minHeight = 2;
                                maxWidth = 3;
                                minWidth = 2;
                                break;
                            case 'countdown':
                                maxHeight = 4;
                                minHeight = 2;
                                maxWidth = 3;
                                minWidth = 2;
                                break;
                            default:
                                break;
                        }

                        const randomColor = getRandomColor();
                        return {
                            id: t.TIL_ID,
                            color: randomColor.color,
                            colorId: randomColor.id,
                            position_x_desktop: 0,
                            position_y_desktop: 0,
                            width_desktop: 1,
                            height_desktop: 1,
                            position_x_mobile: 0,
                            position_y_mobile: 0,
                            width_mobile: 1,
                            height_mobile: 1,
                            max_height: maxHeight,
                            min_height: minHeight,
                            max_width: maxWidth,
                            min_width: minWidth,
                            data: JSON.parse(t.TIL_DATA || '[]'),
                            type: t.TYP_NAME,
                            isOptional: true,
                            availableForPresta: t.TIL_AVAILABLE_FOR_PRESTA,
                            notification: t.NOTIFICATIONS ? JSON.parse(t.NOTIFICATIONS) : null,
                            evenement: t.EVENEMENTS ? JSON.parse(t.EVENEMENTS) : null,
                            createdAt: t.TIL_CREATED_AT
                        };
                    });
                    setUnusedTiles(formattedUnused);
                } catch (err) {
                    console.error("Erreur lors du chargement des unused tiles :", err);
                } finally {
                    setLoading(false);
                }
            }
            setLoading(false);
        };
    
        fetchTiles();
    }, [authenticated, preferences, userBDDInfo]);

    useEffect(() => {
        if (tilesState.length === 0 || layoutReady) return;
    
        const buildLayout = (mode) => tilesState.map(tile => ({
            i: tile.id.toString(),
            x: mode === 'desktop' ? tile.position_x_desktop : tile.position_x_mobile,
            y: mode === 'desktop' ? tile.position_y_desktop : tile.position_y_mobile,
            w: mode === 'desktop' ? tile.width_desktop : tile.width_mobile,
            h: mode === 'desktop' ? tile.height_desktop : tile.height_mobile,
            maxH: tile.max_height,
            minH: tile.min_height,
            maxW: tile.max_width,
            minW: tile.min_width,
            color: tile.color
        }));
    
        const newDesktopLayout = buildLayout('desktop');
        const newMobileLayout = buildLayout('mobile');
        
        setDesktopLayout(newDesktopLayout);
        setMobileLayout(newMobileLayout);
        setLayoutReady(true);
    }, [tilesState, layoutReady]);

    // Sauvegarder les layouts initiaux une fois que le layout est prêt (et après un court délai pour laisser ReactGridLayout se stabiliser)
    useEffect(() => {
        if (layoutReady && !hasInitialLayouts && desktopLayout.length > 0 && mobileLayout.length > 0) {
            // Petit délai pour laisser ReactGridLayout stabiliser les layouts
            const timer = setTimeout(() => {
                setInitialDesktopLayout(JSON.parse(JSON.stringify(desktopLayout)));
                setInitialMobileLayout(JSON.parse(JSON.stringify(mobileLayout)));
                setInitialUnusedTiles(JSON.parse(JSON.stringify(unusedTiles.map(t => t.id).sort())));
                setHasInitialLayouts(true);
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [layoutReady, desktopLayout, mobileLayout, unusedTiles, hasInitialLayouts]);
    
    const visibleTiles = useMemo(() => {
        const seen = new Set();
        return tilesState
            .filter(tile => !unusedTiles.some(rt => rt.id === tile.id))
            .filter(tile => {
                if (seen.has(tile.id)) return false;
                seen.add(tile.id);
                return true;
            });
    }, [tilesState, unusedTiles]);

    const getNextAvailablePositions = (tiles, wDesktop, hDesktop, wMobile, hMobile) => {
        const occupiedDesktop = new Set();
        const occupiedMobile = new Set();
    
        tiles.forEach(tile => {
            for (let dx = 0; dx < tile.width_desktop; dx++) {
                for (let dy = 0; dy < tile.height_desktop; dy++) {
                    occupiedDesktop.add(`${tile.position_x_desktop + dx},${tile.position_y_desktop + dy}`);
                }
            }
            for (let dx = 0; dx < tile.width_mobile; dx++) {
                for (let dy = 0; dy < tile.height_mobile; dy++) {
                    occupiedMobile.add(`${tile.position_x_mobile + dx},${tile.position_y_mobile + dy}`);
                }
            }
        });
    
        const desktopCols = 10;
        const mobileCols = 2;
    
        let foundDesktop = null;
        let foundMobile = null;
    
        // Trouver position desktop
        for (let y = 0; y < 100; y++) {
            for (let x = 0; x <= desktopCols - wDesktop; x++) {
                let spaceFree = true;
                for (let dx = 0; dx < wDesktop; dx++) {
                    for (let dy = 0; dy < hDesktop; dy++) {
                        if (occupiedDesktop.has(`${x + dx},${y + dy}`)) {
                            spaceFree = false;
                            break;
                        }
                    }
                    if (!spaceFree) break;
                }
                if (spaceFree) {
                    foundDesktop = { x, y };
                    break;
                }
            }
            if (foundDesktop) break;
        }
    
        // Trouver position mobile
        for (let y = 0; y < 100; y++) {
            for (let x = 0; x <= mobileCols - wMobile; x++) {
                let spaceFree = true;
                for (let dx = 0; dx < wMobile; dx++) {
                    for (let dy = 0; dy < hMobile; dy++) {
                        if (occupiedMobile.has(`${x + dx},${y + dy}`)) {
                            spaceFree = false;
                            break;
                        }
                    }
                    if (!spaceFree) break;
                }
                if (spaceFree) {
                    foundMobile = { x, y };
                    break;
                }
            }
            if (foundMobile) break;
        }
    
        // Fallback
        return {
            desktop: foundDesktop || { x: 0, y: 0 },
            mobile: foundMobile || { x: 0, y: 0 }
        };
    };    

    const removeTileFromLayout = (tileId) => {
        const tileToRemove = tilesState.find(t => t.id === tileId);
        if (!tileToRemove) return;
        
        setUnusedTiles(prev => [...prev, tileToRemove]);
    
        setCurrentLayout(prev => prev.filter(item => item.i !== tileId.toString()));
    };

    const restoreTileToLayout = (tileId) => {
        const tile = unusedTiles.find(t => t.id === tileId);
        if (!tile) return;
    
        setUnusedTiles(prev => prev.filter(t => t.id !== tileId));

        // Calculer les positions dispo AVANT de modifier les états
        const layoutTiles = tilesState.filter(t => !unusedTiles.some(rt => rt.id === t.id));
        const { desktop: desktopPos, mobile: mobilePos } = getNextAvailablePositions(
            layoutTiles,
            tile.width_desktop,
            tile.height_desktop,
            tile.width_mobile,
            tile.height_mobile
        );
    
        // Mettre à jour les coordonnées dans tilesState (source des positions)
        setTilesState(prev => {
            const alreadyExists = prev.some(t => t.id === tileId);
            const updatedTile = {
                ...tile,
                position_x_desktop: desktopPos.x,
                position_y_desktop: desktopPos.y,
                width_desktop: tile.width_desktop,
                height_desktop: tile.height_desktop,
                position_x_mobile: mobilePos.x,
                position_y_mobile: mobilePos.y,
                width_mobile: tile.width_mobile,
                height_mobile: tile.height_mobile
            };
    
            if (alreadyExists) {
                return prev.map(t => t.id === tileId ? updatedTile : t);
            } else {
                return [...prev, updatedTile];
            }
        });

        if (isDesktopMode) {
            setDesktopLayout(prev => [...prev, {
                i: tile.id.toString(),
                x: desktopPos.x,
                y: desktopPos.y,
                w: tile.width_desktop,
                h: tile.height_desktop,
                maxH: tile.max_height,
                minH: tile.min_height,
                maxW: tile.max_width,
                minW: tile.min_width,
                color: tile.color
            }]);
        } else {
            setMobileLayout(prev => [...prev, {
                i: tile.id.toString(),
                x: mobilePos.x,
                y: mobilePos.y,
                w: tile.width_mobile,
                h: tile.height_mobile,
                maxH: tile.max_height,
                minH: tile.min_height,
                maxW: tile.max_width,
                minW: tile.min_width,
                color: tile.color
            }]);
        }
    };

    function compactLayoutVertically(layout, cols) {
        const items = [...layout].sort((a, b) => {
            if (a.x === b.x) return a.y - b.y;
            return a.x - b.x;
        });
    
        const occupied = new Set();
        const updated = [];
    
        for (const item of items) {
            let y = 0;
            while (true) {
                let collision = false;
                for (let dx = 0; dx < item.w; dx++) {
                    for (let dy = 0; dy < item.h; dy++) {
                        const key = `${item.x + dx},${y + dy}`;
                        if (occupied.has(key)) {
                            collision = true;
                            break;
                        }
                    }
                    if (collision) break;
                }
                if (!collision) break;
                y++;
            }
    
            const newItem = {
                ...item,
                y
            };
    
            for (let dx = 0; dx < newItem.w; dx++) {
                for (let dy = 0; dy < newItem.h; dy++) {
                    occupied.add(`${newItem.x + dx},${newItem.y + dy}`);
                }
            }
    
            updated.push(newItem);
        }
    
        return updated;
    };    

    const handleSave = async () => {
        if (!authenticated || !userBDDInfo || !userADInfo) return;

        const userId = userBDDInfo.data[0].USR_ID;

        const getTile = (id) => tilesState.find(tile => tile.id.toString() === id);

        const visibleTileIds = tilesState
            .map(tile => tile.id.toString())
            .filter(id => !unusedTiles.some(t => t.id.toString() === id));

        const compactedDesktopLayout = compactLayoutVertically(desktopLayout, 10);
        const compactedMobileLayout = compactLayoutVertically(mobileLayout, 2);

        setDesktopLayout(compactedDesktopLayout);
        setMobileLayout(compactedMobileLayout);

        const updatedPreferences = visibleTileIds.map(id => {
            const desktopItem = compactedDesktopLayout.find(d => d.i === id);
            const mobileItem = compactedMobileLayout.find(m => m.i === id);
            const tile = getTile(id);
    
            return {
                tile_id: parseInt(id),
                color_id: tile?.colorId,
                position_x_desktop: desktopItem?.x ?? 0,
                position_y_desktop: desktopItem?.y ?? 0,
                width_desktop: desktopItem?.w ?? 1,
                height_desktop: desktopItem?.h ?? 1,
                position_x_mobile: mobileItem?.x ?? 0,
                position_y_mobile: mobileItem?.y ?? 0,
                width_mobile: mobileItem?.w ?? 1,
                height_mobile: mobileItem?.h ?? 1,
                max_height: tile?.max_height ?? 1,
                min_height: tile?.min_height ?? 1,
                max_width: tile?.max_width ?? 1,
                min_width: tile?.min_width ?? 1,
            };
        });    

        try {
            await upsertUserPreferences(userId, updatedPreferences);
            // Mettre à jour les layouts initiaux après sauvegarde
            setInitialDesktopLayout(JSON.parse(JSON.stringify(compactedDesktopLayout)));
            setInitialMobileLayout(JSON.parse(JSON.stringify(compactedMobileLayout)));
            setInitialUnusedTiles(JSON.parse(JSON.stringify(unusedTiles.map(t => t.id).sort())));
            setHasInitialLayouts(true);
            navigate('/');
        } catch (err) {
            console.error("Erreur lors de la sauvegarde des préférences", err);
        }
    };

    const handleReset = async () => {
        try {
            const defaultPreferences = await getDefaultTiles(isExternal ? 'extern' : 'intern', userBDDInfo.data[0].USR_ID);
            const preferencesData = defaultPreferences.data;

            const mapped = MapPreferencesToTiles(preferencesData);
            setTilesState(mapped);
            // Réinitialiser les layouts initiaux à vide pour qu'ils soient régénérés
            setInitialDesktopLayout([]);
            setInitialMobileLayout([]);
            setInitialUnusedTiles([]);
            setHasInitialLayouts(false);
            setLayoutReady(false);
        } catch (err) {
            console.error("Erreur lors de la réinitialisation des préférences", err);
        }
    };

    const handleResetClick = () => setShowResetConfirmation(true);
    const handleConfirmReset = async () => {
        await handleReset();
        setShowResetConfirmation(false);
    };
    const handleCancelReset = () => setShowResetConfirmation(false);

    const updateTileColor = (tileId, newColor) => {
        const idStr = tileId.toString();
    
        const desktopItem = desktopLayout.find(l => l.i === idStr);
        const mobileItem = mobileLayout.find(l => l.i === idStr);
    
        setTilesState(prev =>
            prev.map(tile => {
                if (tile.id !== tileId) return tile;
    
                return {
                    ...tile,
                    color: newColor,
                    position_x_desktop: desktopItem?.x ?? tile.position_x_desktop,
                    position_y_desktop: desktopItem?.y ?? tile.position_y_desktop,
                    width_desktop: desktopItem?.w ?? tile.width_desktop,
                    height_desktop: desktopItem?.h ?? tile.height_desktop,
                    position_x_mobile: mobileItem?.x ?? tile.position_x_mobile,
                    position_y_mobile: mobileItem?.y ?? tile.position_y_mobile,
                    width_mobile: mobileItem?.w ?? tile.width_mobile,
                    height_mobile: mobileItem?.h ?? tile.height_mobile
                };
            })
        );
    
        setDesktopLayout(prev =>
            prev.map(tile =>
                tile.i === idStr ? { ...tile, color: newColor } : tile
            )
        );
        setMobileLayout(prev =>
            prev.map(tile =>
                tile.i === idStr ? { ...tile, color: newColor } : tile
            )
        );
    };

    // Fonction pour normaliser un layout (ne garder que les propriétés importantes)
    const normalizeLayout = (layout) => {
        return layout.map(item => ({
            i: item.i,
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
            color: item.color
        })).sort((a, b) => a.i.localeCompare(b.i));
    };

    const hasUnsavedChanges = () => {
        // Comparer seulement les propriétés importantes de position et de taille
        const desktopChanged = JSON.stringify(normalizeLayout(desktopLayout)) !== JSON.stringify(normalizeLayout(initialDesktopLayout));
        const mobileChanged = JSON.stringify(normalizeLayout(mobileLayout)) !== JSON.stringify(normalizeLayout(initialMobileLayout));
        
        // Comparer les unusedTiles (liste des IDs)
        const currentUnusedTileIds = unusedTiles.map(t => t.id).sort();
        const unusedTilesChanged = JSON.stringify(currentUnusedTileIds) !== JSON.stringify(initialUnusedTiles);
        
        return desktopChanged || mobileChanged || unusedTilesChanged;
    };

    const attemptNavigation = () => {
        if (hasUnsavedChanges()) {
            setShowUnsavedChangesModal(true);
        } else {
            navigate('/');
        }
    };    

    const handleCancel = () => {
        attemptNavigation();
    };

    // Gérer l'événement beforeunload pour avertir si des modifications non sauvegardées
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            // Normaliser les layouts pour comparer seulement les propriétés importantes
            const normalizeLayout = (layout) => {
                return layout.map(item => ({
                    i: item.i,
                    x: item.x,
                    y: item.y,
                    w: item.w,
                    h: item.h,
                    color: item.color
                })).sort((a, b) => a.i.localeCompare(b.i));
            };

            const desktopChanged = JSON.stringify(normalizeLayout(desktopLayout)) !== JSON.stringify(normalizeLayout(initialDesktopLayout));
            const mobileChanged = JSON.stringify(normalizeLayout(mobileLayout)) !== JSON.stringify(normalizeLayout(initialMobileLayout));
            
            // Comparer les unusedTiles
            const currentUnusedTileIds = unusedTiles.map(t => t.id).sort();
            const unusedTilesChanged = JSON.stringify(currentUnusedTileIds) !== JSON.stringify(initialUnusedTiles);
            
            if (desktopChanged || mobileChanged || unusedTilesChanged) {
                e.preventDefault();
                e.returnValue = 'Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [desktopLayout, mobileLayout, initialDesktopLayout, initialMobileLayout, unusedTiles, initialUnusedTiles]);

    const handleConfirmLeave = () => {
        setShowUnsavedChangesModal(false);
        navigate('/');
    };

    const handleCancelLeave = () => {
        setShowUnsavedChangesModal(false);
    };

    const handleProposeTile = async (formData) => {
        try {
            await proposeTile(userBDDInfo.data[0].USR_ID, formData);
            addToast('Succès', 'Votre proposition de tuile a été envoyée avec succès. Elle sera traitée dans les plus brefs délais.', null, null, 'success');
        } catch (err) {
            console.error("Erreur lors de la proposition de la tuile", err);
            addToast('Erreur', 'Impossible de proposer la tuile.', null, null, 'error');
        }
    };

    if (loading || !layoutReady) {
        return <Loader />;
    }

    if (!currentLayout) return null;
    
    return (
        <div className="flex bg-blanc dark:bg-noir text-noir dark:text-blanc overflow-hidden">
            <Sidebar 
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
                unusedTiles={unusedTiles}
                onRestore={restoreTileToLayout}
                colorPickerTileId={colorPickerTileId}
                updateTileColor={updateTileColor}
                tilesState={tilesState}
                setTilesState={setTilesState}
            />
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-noir/60 z-5 md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            <div className={`bg-blanc dark:bg-noir flex flex-col h-screen w-full overflow-auto 
                transition-[margin] duration-500 ease-in-out 
                ${isSidebarOpen ? 'ml-0 md:ml-80 z-0 md:z-10' : 'ml-0 z-10'}`}
            >
                {/* Floating elements */}
                <div className="fixed top-4 left-4 z-50">
                    {!isSidebarOpen && (
                        <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-lg hover:bg-noir/10 dark:hover:bg-gris/40 cursor-pointer">
                            <ChevronRight size={24} />
                        </button>
                    )}
                </div>

                <div className="fixed top-4 right-4 w-80 z-50">
                    <ModeToggle
                        value={!isDesktopMode}
                        onChange={setIsDesktopMode}
                        optionLeft="Mode ordinateur"
                        optionRight="Mode mobile"
                    />
                </div>

                <div className="flex-1 overflow-auto p-4 pt-20 bg-gray-100 dark:bg-dark">
                    <GridEditing
                        user={userBDDInfo && userADInfo ? { userBDDInfo, userADInfo } : null}
                        tiles={visibleTiles}
                        layout={currentLayout}
                        setLayout={setCurrentLayout}
                        isEditing={true}
                        isDesktopMode={isDesktopMode}
                        removeFromGrid={removeTileFromLayout}
                        setColorPickerTileId={setColorPickerTileId}
                    />
                </div>
            </div>

            {/* Boutons d'action flottants centrés en bas */}
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
                {!showResetConfirmation ? (
                    <div className="flex gap-4 justify-center bg-blanc/80 dark:bg-noir/80 backdrop-blur px-4 py-3 rounded-xl shadow-lg">
                        <div className="flex flex-col items-center gap-1 min-w-[64px]">
							<TooltipComponent title="Proposer une nouvelle tuile">
								<button
									aria-label="Proposer une tuile"
									onClick={() => setShowProposeModal(true)}
									className="p-3 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors cursor-pointer"
								>
									<Plus size={20} className="text-noir dark:text-blanc" />
								</button>
							</TooltipComponent>
                            <span className="text-xs text-noir dark:text-blanc select-none">Proposer</span>
                        </div>
                        <div className="flex flex-col items-center gap-1 min-w-[64px]">
							<TooltipComponent title="Sauvegarder et retourner au HUB">
								<button
									aria-label="Sauvegarder"
									onClick={handleSave}
									className="p-3 rounded-lg hover:bg-green-100 dark:hover:bg-green-900 transition-colors cursor-pointer"
								>
									<Save size={20} className="text-noir dark:text-blanc" />
								</button>
							</TooltipComponent>
                            <span className="text-xs text-noir dark:text-blanc select-none">Sauvegarder</span>
                        </div>
                        <div className="flex flex-col items-center gap-1 min-w-[64px]">
							<TooltipComponent title="Réinitialiser l'interface par défaut">
								<button
									aria-label="Réinitialiser"
									onClick={handleResetClick}
									className="p-3 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors cursor-pointer"
								>
									<RefreshCcw size={20} className="text-noir dark:text-blanc" />
								</button>
							</TooltipComponent>
                            <span className="text-xs text-noir dark:text-blanc select-none">Réinitialiser</span>
                        </div>
                        <div className="flex flex-col items-center gap-1 min-w-[64px]">
							<TooltipComponent title="Annuler et retourner au HUB">
								<button
									aria-label="Annuler"
									onClick={handleCancel}
									className="p-3 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 transition-colors cursor-pointer"
								>
									<CircleX size={20} className="text-noir dark:text-blanc" />
								</button>
							</TooltipComponent>
                            <span className="text-xs text-noir dark:text-blanc select-none">Annuler</span>
                        </div>
                    </div>
                ) : (
                    <div className="flex gap-4 justify-center bg-blanc/80 dark:bg-noir/80 backdrop-blur px-4 py-3 rounded-xl shadow-lg">
                        <div className="flex flex-col items-center gap-1 min-w-[64px]">
                            <TooltipComponent title="Confirmer la réinitialisation">
                                <button
                                    aria-label="Confirmer la réinitialisation"
                                    onClick={handleConfirmReset}
                                    className="p-3 rounded-lg hover:bg-green-100 dark:hover:bg-green-900 transition-colors cursor-pointer"
                                >
                                    <Check size={20} className="text-noir dark:text-blanc" />
                                </button>
                            </TooltipComponent>
                            <span className="text-xs text-noir dark:text-blanc select-none">Confirmer</span>
                        </div>
                        <div className="flex flex-col items-center gap-1 min-w-[64px]">
                            <TooltipComponent title="Annuler la réinitialisation">
                                <button
                                    aria-label="Annuler la réinitialisation"
                                    onClick={handleCancelReset}
                                    className="p-3 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 transition-colors cursor-pointer"
                                >
                                    <CircleX size={20} className="text-noir dark:text-blanc" />
                                </button>
                            </TooltipComponent>
                            <span className="text-xs text-noir dark:text-blanc select-none">Annuler</span>
                        </div>
                    </div>
                )}
            </div>

            <ProposeTileModal 
                isOpen={showProposeModal}
                onClose={() => setShowProposeModal(false)}
                onSubmit={handleProposeTile}
            />

            {/* Modal de confirmation pour les changements non sauvegardés */}
            {showUnsavedChangesModal && (
                <div className="fixed inset-0 bg-noir/50 dark:bg-dark/70 flex items-center justify-center z-50">
                    <div className="bg-blanc dark:bg-noir rounded-xl p-6 w-full max-w-md relative mx-4">
                        <h2 className="text-2xl font-bold text-noir dark:text-blanc mb-4">
                            Modifications non sauvegardées
                        </h2>
                        <p className="text-noir dark:text-blanc mb-6">
                            Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter sans sauvegarder ?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={handleCancelLeave}
                                className="px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gris/40 transition-colors text-noir dark:text-blanc"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmLeave}
                                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-blanc transition-colors"
                            >
                                Quitter sans sauvegarder
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EditInterface;